import { useEffect, useState, useCallback } from 'react';
import { isTelegramMiniApp, miniApp } from '../lib/telegram';
import { useSignal } from '@telegram-apps/sdk-react';

const STORAGE_KEY = 'cherry-theme';

function applyTheme(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark);
}

function useTelegramDark(): boolean | undefined {
  const isTg = isTelegramMiniApp() && miniApp.isMounted();
  const signal = useSignal(miniApp.isDark);
  return isTg ? signal : undefined;
}

export function useDarkMode() {
  const tgDark = useTelegramDark();

  // nosemgrep: semgrep.cherry-storage-usestate-init -- SPA-only (no SSR/hydration today); revisit at Mathom fold-in (CM-500); audited 2026-08-18
  const [userPref, setUserPref] = useState<'dark' | 'light' | null>(
    () => localStorage.getItem(STORAGE_KEY) as 'dark' | 'light' | null,
  );

  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  const isDark =
    userPref !== null
      ? userPref === 'dark'
      : tgDark !== undefined
        ? tgDark
        : systemDark;

  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  useEffect(() => {
    if (isTelegramMiniApp()) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggle = useCallback(() => {
    const next = isDark ? 'light' : 'dark';
    setUserPref(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, [isDark]);

  return { isDark, toggle };
}
